import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import FeaturedSection from '../components/Sidebar/FeaturedSection';
import SearchSection from '../components/Sidebar/SearchSection';
import TagSection from '../components/Sidebar/TagSection';
import SocialMediaLinks from '../components/SocialMediaLinks/SocialMediaLinks';
import { fetchPostBySlug } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { resolveImageUrl } from '../utils/imageUrl';
import { applySocialMeta } from '../utils/socialMeta';
import { buildSharePreviewUrl } from '../utils/shareUrl';
import { buildSubscriberSignInPath, isSubscriberOnlyContent } from '../utils/subscriberAccess';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import './BlogDetail.css';

const BlogDetail = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { canAccessExclusiveContent } = useAuth();
  const signInPath = buildSubscriberSignInPath(`/blog/${slug}`);

  useEffect(() => {
    const loadPost = async () => {
      try {
        setLoading(true);
        const data = await fetchPostBySlug(slug);
        setPost(data);
        setError(null);
      } catch (err) {
        console.error('Failed to load post:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [slug]);

  useEffect(() => {
    if (!post) return;

    const descriptionSource = post.excerpt || post.body || '';
    const plainDescription = descriptionSource
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 180);

    applySocialMeta({
      title: post.meta_title || post.title,
      description: post.meta_description || plainDescription,
      url: window.location.href,
      image: post.cover_image_url || post.cover_image,
      type: 'article',
    });
  }, [post]);

  if (loading) {
    return (
      <div className="blog-detail-page">
        <div className="blog-detail-container">
          <div className="blog-detail-content">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-detail-page">
        <div className="blog-detail-container">
          <div className="blog-detail-content blog-not-found">
            <h1>Post Not Found</h1>
            <p>The post you're looking for doesn't exist or has been removed.</p>
            <Link to="/blog" className="back-to-blog">
              <i className="fas fa-arrow-left"></i>
              Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Get the cover image URL for posts
  const getPostImageUrl = (post) => {
    return resolveImageUrl(post.cover_image_url || post.cover_image);
  };

  const postImageUrl = getPostImageUrl(post);

  return (
    <div className="blog-detail-page">
      <div className="blog-detail-container">
        <main className="blog-detail-content">
          <nav className="breadcrumb">
            <Link to="/">Home</Link>
            <span className="breadcrumb-separator">/</span>
            <Link to="/blog">It Guhso</Link>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">{post.title}</span>
          </nav>

          <div className="blog-header">
            <div className="blog-image">
              {postImageUrl ? (
                <img 
                  src={postImageUrl}
                  alt={post.title}
                  className="blog-detail-image"
                  loading="eager"
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    e.target.style.display = 'none';
                    e.target.parentNode.classList.add('no-image');
                  }}
                />
              ) : (
                <div className="post-placeholder-image">
                  <i className="fas fa-newspaper"></i>
                </div>
              )}
            </div>
          <div className="blog-info">
            <h1 className="blog-title">{post.title}</h1>
            {isSubscriberOnlyContent(post) && (
              <div className="blog-meta">
                <i className="fas fa-lock"></i>
                <span>Subscriber-only article</span>
              </div>
            )}
            {post.published_at && (
              <div className="blog-meta">
                  <i className="fas fa-calendar"></i>
                  <span>
                    {new Date(post.published_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          {isSubscriberOnlyContent(post) && (
            <div className="blog-body">
              <p>{post.excerpt || 'This article is available to paid subscribers.'}</p>
              <p>
                {canAccessExclusiveContent
                  ? 'Refresh this page if your subscription was just activated.'
                  : <>Subscriber access starts at $3.99 monthly or $38.50 yearly. <Link to={signInPath}>Sign in or start a paid subscription</Link>.</>}
              </p>
            </div>
          )}

          {post.body && !isSubscriberOnlyContent(post) && (
            <div 
              className="blog-body" 
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.body) }}
              data-html-rendered="true"
            />
          )}
          
          {/* HTML content rendering fix applied */}
          {post.tags && post.tags.length > 0 && (
            <div className="blog-tags">
              {post.tags.map(tag => (
                <span key={tag} className="blog-tag">#{tag}</span>
              ))}
            </div>
          )}

          {/* Social Media Links */}
          <div className="blog-social-section">
            <SocialMediaLinks
              episode={{
                ...post,
                share_type: 'post',
                share_url: post.share_url || buildSharePreviewUrl('post', post.slug),
              }}
            />
          </div>
        </main>

        <aside className="blog-sidebar blog-detail-sidebar">
          <FeaturedSection />
          <SearchSection />
          <TagSection />
        </aside>
      </div>
    </div>
  );
};

export default BlogDetail;
